import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { defer as observableDefer, Observable, of as observableOf, throwError as observableThrowError } from 'rxjs';
import { timeout, map, catchError } from 'rxjs/operators';
import { timeoutError } from '../../models/timeout-error';
import { TimeoutLimit } from '../../models/timeout-limit';
import { GlobalEvent, GlobalEventService } from '../global-event-service/global-event.service';
import { GuestProfileResponse } from '../../dtos/response/mileage-plan-profile-lookup-response/guest-profile-response';
import { MileagePlanProfileLookupResponse } from '../../dtos/response/mileage-plan-profile-lookup-response/mileage-plan-profile-lookup-response';
import { MileagePlanProfileLookupStatus } from '../../dtos/response/mileage-plan-profile-lookup-response/mileage-plan-profile-lookup-status';
import { SearchByNameAndDobRequest } from '../../dtos/request/mileage-plan-profile-search-request/search-by-name-and-dob-request';
import { SearchByPhoneNumberRequest } from '../../dtos/request/mileage-plan-profile-search-request/search-by-phone-number-request';
import { MileagePlanProfileSearchResponse } from '../../dtos/response/mileage-plan-profile-search-response/mileage-plan-profile-search-response';
import { MileagePlanProfileSearchStatus } from '../../dtos/response/mileage-plan-profile-search-response/mileage-plan-profile-search-status';
import { PersonLookup } from '../../dtos/response/mileage-plan-profile-search-response/person-lookup';

export interface MileagePlanProfileServiceAPI {
  mileagePlanProfileLookup(
    mileagePlanNumber: string,
    airlineCode: string,
    autoFillOnLookup: boolean
  ): Observable<MileagePlanProfileLookupResponse>;

  mileagePlanProfileSearchByPhoneNumber(
    searchByPhoneNumberRequest: SearchByPhoneNumberRequest
  ): Observable<MileagePlanProfileSearchResponse>;

  mileagePlanProfileSearchByNameAndDob(searchByNameAndDobRequest: SearchByNameAndDobRequest): Observable<MileagePlanProfileSearchResponse>;
}

@Injectable({
  providedIn: 'root',
})
export class MileagePlanProfileService implements MileagePlanProfileServiceAPI {
  private readonly http = inject(HttpClient);
  private readonly eventService = inject(GlobalEventService);

  public mileagePlanProfileLookup(
    mileagePlanNumber: string,
    airlineCode: string,
    backgroundLookup: boolean = false
  ): Observable<MileagePlanProfileLookupResponse> {
    const options = {
      headers: new HttpHeaders({
        // By default background lookup is false so that loading gif is shown during lookups.
        // Special case is that we want to lookup Mileage Plan profile in the background after
        // a successfull guest info save, so that we can get the most updated mileage plan data
        // for the guest.
        background: backgroundLookup ? 'true' : 'false',
      }),
    };
    return this.http.get<GuestProfileResponse>(`api/mileage-plan-profile/lookup/${mileagePlanNumber}?airline=${airlineCode}`, options).pipe(
      timeout({
        each: TimeoutLimit.MEDIUM,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((mileagePlanProfileResponse) => {
        // Full success scenario
        if (
          mileagePlanProfileResponse.accountLookupResponse?.success === true &&
          mileagePlanProfileResponse.memberInfoLookupResponse?.success === true
        ) {
          return { response: mileagePlanProfileResponse, status: MileagePlanProfileLookupStatus.SUCCESS, isProfileLookup: true };
        }
        // Partial failure scenario
        if (
          mileagePlanProfileResponse.accountLookupResponse?.success === true ||
          mileagePlanProfileResponse.memberInfoLookupResponse?.success === true
        ) {
          return { response: mileagePlanProfileResponse, status: MileagePlanProfileLookupStatus.PARTIAL_FAILURE, isProfileLookup: true };
        } else {
          // Full failure scenario
          return { response: mileagePlanProfileResponse, status: MileagePlanProfileLookupStatus.NO_DATA_RETURNED, isProfileLookup: true };
        }
      }),
      catchError((err) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
          return observableOf({ status: MileagePlanProfileLookupStatus.TIMEOUT, error: 'Timeout error', isProfileLookup: true });
        }
        if (404 === err.status) {
          return observableOf({ status: MileagePlanProfileLookupStatus.NO_DATA_RETURNED, error: err.error, isProfileLookup: true });
        }
        if (400 === err.status) {
          // Bad request error handling, stringify the error so that we can catch the specific error messages
          return observableOf({
            status: MileagePlanProfileLookupStatus.BAD_REQUEST,
            error: JSON.stringify(err.error),
            isProfileLookup: true,
          });
        }
        return observableOf({ status: MileagePlanProfileLookupStatus.SYSTEM_FAILURE, error: err.statusText, isProfileLookup: true });
      })
    );
  }

  public mileagePlanProfileSearchByPhoneNumber(
    searchByPhoneNumberRequest: SearchByPhoneNumberRequest,
    isBackgroundLookup = false
  ): Observable<MileagePlanProfileSearchResponse> {
    const options = {
      headers: new HttpHeaders({
        background: isBackgroundLookup ? 'true' : 'false',
      }),
    };
    return this.http.post<PersonLookup>('api/mileage-plan-profile/search/by-phone-number', searchByPhoneNumberRequest, options).pipe(
      timeout({
        each: TimeoutLimit.SHORT,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((personLookup) => {
        return { response: personLookup, status: MileagePlanProfileSearchStatus.SUCCESS, isProfileSearch: true };
      }),
      catchError((err) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
          return observableOf({ status: MileagePlanProfileSearchStatus.TIMEOUT, error: 'Timeout error', isProfileSearch: true });
        }
        if (404 === err.status) {
          return observableOf({ status: MileagePlanProfileSearchStatus.NO_DATA_RETURNED, error: err.error, isProfileSearch: true });
        }
        return observableOf({ status: MileagePlanProfileSearchStatus.SYSTEM_FAILURE, error: err.error, isProfileSearch: true });
      })
    );
  }

  public mileagePlanProfileSearchByNameAndDob(
    searchByNameAndDobRequest: SearchByNameAndDobRequest,
    isBackgroundLookup = false
  ): Observable<MileagePlanProfileSearchResponse> {
    const options = {
      headers: new HttpHeaders({
        background: isBackgroundLookup ? 'true' : 'false',
      }),
    };
    return this.http.post<PersonLookup>('api/mileage-plan-profile/search/by-name-and-dob', searchByNameAndDobRequest, options).pipe(
      timeout({
        each: TimeoutLimit.SHORT,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((personLookup) => {
        return { response: personLookup, status: MileagePlanProfileSearchStatus.SUCCESS, isProfileSearch: true };
      }),
      catchError((err) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
          return observableOf({ status: MileagePlanProfileSearchStatus.TIMEOUT, error: 'Timeout error', isProfileSearch: true });
        }
        if (404 === err.status) {
          return observableOf({ status: MileagePlanProfileSearchStatus.NO_DATA_RETURNED, error: err.error, isProfileSearch: true });
        }
        return observableOf({ status: MileagePlanProfileSearchStatus.SYSTEM_FAILURE, error: err.error, isProfileSearch: true });
      })
    );
  }
}
